import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Container, Card, Button } from '../../styles/GlobalStyles';

const VerifyContainer = styled(Container)`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: var(--spacing-md);
`;

const VerifyCard = styled(Card)`
  width: 100%;
  max-width: 500px;
  margin: 0;
  text-align: center;
`;

const IconContainer = styled.div`
  font-size: 4rem;
  margin-bottom: var(--spacing-lg);
`;

const Title = styled.h1`
  margin-bottom: var(--spacing-md);
  color: var(--text-primary);
  font-size: 1.875rem;
  font-weight: 700;
`;

const Message = styled.p`
  margin-bottom: var(--spacing-lg);
  color: var(--text-secondary);
  line-height: 1.6;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: var(--spacing-lg) auto;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
  padding: var(--spacing-md);
  border-radius: 4px;
  margin-bottom: var(--spacing-lg);
`;

export const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [countdown, setCountdown] = useState(3);
  const token = searchParams.get('token');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        setErrorMessage('Invalid verification link. No token provided.');
        return;
      }

      try {
        // Store the token in localStorage immediately for auto-login
        localStorage.setItem('token', token);
        
        // Decode the token to get user info
        const tokenParts = token.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          if (payload.email) {
            localStorage.setItem('currentUserEmail', payload.email);
          }
        }
        
        setStatus('success');
        
        // Start countdown and redirect
        const timer = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(timer);
              navigate('/administration-club/dashboard');
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } catch (error: any) {
        setStatus('error');
        setErrorMessage(
          'Email verification failed. The link may have expired or is invalid.'
        );
      }
    };

    verifyEmail();
  }, [token, navigate]);

  if (status === 'verifying') {
    return (
      <VerifyContainer>
        <VerifyCard>
          <LoadingSpinner />
          <Title>Verifying Your Email</Title>
          <Message>Please wait while we verify your email address...</Message>
        </VerifyCard>
      </VerifyContainer>
    );
  }

  if (status === 'error') {
    return (
      <VerifyContainer>
        <VerifyCard>
          <IconContainer>❌</IconContainer>
          <Title>Verification Failed</Title>
          <ErrorMessage>{errorMessage}</ErrorMessage>
          <Message>
            If you continue to experience issues, please contact support or try registering again.
          </Message>
          <Button onClick={() => navigate('/administration-club')}>
            Back to Login
          </Button>
        </VerifyCard>
      </VerifyContainer>
    );
  }

  return (
    <VerifyContainer>
      <VerifyCard>
        <IconContainer>✅</IconContainer>
        <Title>Email Verified!</Title>
        <Message>
          Your email has been successfully verified. You're now logged in and will be redirected to your dashboard.
        </Message>
        <Message style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
          Redirecting in {countdown} seconds...
        </Message>
        <LoadingSpinner />
      </VerifyCard>
    </VerifyContainer>
  );
};
